import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientsAPI, formsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDate, formatDateTime, calculateAge, getCategoryColor, formatRelativeTime } from '../lib/utils';
import { generatePatientSummaryPDF } from '../lib/pdfGenerator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft,
  Edit,
  MoreVertical,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  Heart,
  AlertTriangle,
  FileText,
  Stethoscope,
  Pill,
  TestTube,
  Scan,
  LogOut,
  Paperclip,
  Plus,
  ClipboardList,
  Loader2,
  Sparkles,
  Receipt,
  Download,
  FileDown,
} from 'lucide-react';
import { toast } from 'sonner';

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [patientRes, timelineRes, attachmentsRes, templatesRes] = await Promise.all([
        patientsAPI.getById(id),
        patientsAPI.getTimeline(id),
        patientsAPI.getAttachments(id),
        formsAPI.getTemplates({ is_active: true }),
      ]);
      setPatient(patientRes.data);
      setTimeline(timelineRes.data);
      setAttachments(attachmentsRes.data);
      setTemplates(templatesRes.data);
    } catch (err) {
      toast.error('Failed to load patient data');
      navigate('/patients');
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    try {
      await patientsAPI.delete(id);
      toast.success('Patient deleted');
      navigate('/patients');
    } catch (err) {
      toast.error('Failed to delete patient');
    }
  };

  const handleGenerateSummaryPDF = async () => {
    setGeneratingPDF(true);
    try {
      const response = await patientsAPI.getSummary(id);
      generatePatientSummaryPDF(response.data);
      toast.success('Patient summary PDF generated!');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
    setGeneratingPDF(false);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      registration: ClipboardList,
      consultation: Stethoscope,
      progress: FileText,
      prescription: Pill,
      lab: TestTube,
      imaging: Scan,
      discharge: LogOut,
      attachment: Paperclip,
      clinical_note: Sparkles,
      billing: Receipt,
    };
    return icons[category] || FileText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Patient not found</p>
        <Button onClick={() => navigate('/patients')} className="mt-4">
          Back to Patients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="patient-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/patients')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="font-bold text-xl text-primary">
                {patient.first_name[0]}{patient.last_name[0]}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {patient.first_name} {patient.last_name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <code className="text-sm bg-muted px-2 py-0.5 rounded">{patient.patient_id}</code>
                <Badge variant="outline">{patient.gender}</Badge>
                {patient.blood_type && (
                  <Badge variant="secondary">{patient.blood_type}</Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          {/* AI Clinical Notes */}
          {hasPermission('clinical_notes:create') && (
            <Button 
              variant="outline" 
              onClick={() => navigate(`/patients/${id}/clinical-notes`)}
              className="gap-2"
              data-testid="clinical-notes-btn"
            >
              <Sparkles className="h-4 w-4" />
              AI Notes
            </Button>
          )}

          {/* Invoices */}
          {hasPermission('invoices:read') && (
            <Button 
              variant="outline" 
              onClick={() => navigate(`/patients/${id}/invoices`)}
              className="gap-2"
              data-testid="invoices-btn"
            >
              <Receipt className="h-4 w-4" />
              Invoices
            </Button>
          )}

          {/* New Submission */}
          {hasPermission('submissions:create') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-2" data-testid="new-submission-btn">
                  <Plus className="h-4 w-4" />
                  New Entry
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {templates.map((template) => (
                  <DropdownMenuItem
                    key={template.id}
                    onClick={() => navigate(`/forms/submit/${template.id}?patient=${id}`)}
                    data-testid={`template-${template.id}`}
                  >
                    {template.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" data-testid="patient-actions-btn">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={handleGenerateSummaryPDF}
                disabled={generatingPDF}
                data-testid="generate-summary-btn"
              >
                <FileDown className="h-4 w-4 mr-2" />
                {generatingPDF ? 'Generating...' : 'Download Summary PDF'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {hasPermission('patients:update') && (
                <DropdownMenuItem onClick={() => navigate(`/patients/${id}/edit`)} data-testid="edit-patient-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Patient
                </DropdownMenuItem>
              )}
              {hasPermission('patients:delete') && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-patient-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Patient
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Info */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Patient Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date of Birth</p>
                <p className="font-medium">{formatDate(patient.date_of_birth)} ({calculateAge(patient.date_of_birth)} years)</p>
              </div>
            </div>

            {patient.phone && (
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{patient.phone}</p>
                </div>
              </div>
            )}

            {patient.email && (
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{patient.email}</p>
                </div>
              </div>
            )}

            {patient.address && (
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{patient.address}</p>
                </div>
              </div>
            )}

            {patient.emergency_contact && (
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Emergency Contact</p>
                  <p className="font-medium">{patient.emergency_contact}</p>
                </div>
              </div>
            )}

            <Separator />

            {/* Allergies */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-medium">Allergies</span>
              </div>
              {patient.allergies && patient.allergies.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {patient.allergies.map((allergy, idx) => (
                    <Badge key={idx} variant="destructive" className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                      {allergy}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No known allergies</p>
              )}
            </div>

            {/* Active Problems */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Heart className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium">Active Problems</span>
              </div>
              {patient.active_problems && patient.active_problems.length > 0 ? (
                <div className="space-y-1">
                  {patient.active_problems.map((problem, idx) => (
                    <p key={idx} className="text-sm">• {problem}</p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">None documented</p>
              )}
            </div>

            {/* Medications */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Pill className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">Current Medications</span>
              </div>
              {patient.medications && patient.medications.length > 0 ? (
                <div className="space-y-1">
                  {patient.medications.map((med, idx) => (
                    <p key={idx} className="text-sm">• {med}</p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">None documented</p>
              )}
            </div>

            {patient.insurance_info && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Insurance</span>
                  </div>
                  <p className="text-sm">{patient.insurance_info.provider}</p>
                  <p className="text-xs text-muted-foreground">Policy: {patient.insurance_info.policy_number}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Timeline & Files */}
        <Card className="lg:col-span-2">
          <Tabs defaultValue="timeline">
            <CardHeader className="pb-0">
              <TabsList>
                <TabsTrigger value="timeline" data-testid="timeline-tab">Timeline</TabsTrigger>
                <TabsTrigger value="files" data-testid="files-tab">Files ({attachments.length})</TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent className="pt-4">
              <TabsContent value="timeline" className="mt-0">
                <ScrollArea className="h-[500px] pr-4">
                  {timeline.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
                      <h3 className="text-lg font-medium mb-1">No records yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Start by creating a new entry for this patient
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {timeline.map((item, index) => {
                        const IconComponent = getCategoryIcon(item.category || item.type);
                        return (
                          <div key={item.id} className="timeline-item" data-testid={`timeline-item-${item.id}`}>
                            <div className={cn(
                              'timeline-icon',
                              getCategoryColor(item.category || item.type)
                            )}>
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <Card className="card-interactive">
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-medium">
                                        {item.form_name || item.filename || item.note_type?.toUpperCase() || item.invoice_number}
                                      </h4>
                                      <Badge variant="secondary" className={cn('text-xs', getCategoryColor(item.category || item.type))}>
                                        {item.category || item.type}
                                      </Badge>
                                      {item.status && (
                                        <Badge variant="outline" className="text-xs">
                                          {item.status}
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      by {item.submitted_by || item.uploaded_by}
                                    </p>
                                    
                                    {/* Show submission data preview */}
                                    {item.data && Object.keys(item.data).length > 0 && (
                                      <div className="mt-3 p-3 bg-muted rounded-lg">
                                        {Object.entries(item.data).slice(0, 3).map(([key, value]) => (
                                          <div key={key} className="text-sm">
                                            <span className="text-muted-foreground capitalize">
                                              {key.replace(/_/g, ' ')}:
                                            </span>{' '}
                                            <span className="font-medium">
                                              {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value).slice(0, 100)}
                                            </span>
                                          </div>
                                        ))}
                                        {Object.keys(item.data).length > 3 && (
                                          <p className="text-xs text-muted-foreground mt-1">
                                            +{Object.keys(item.data).length - 3} more fields
                                          </p>
                                        )}
                                      </div>
                                    )}

                                    {/* Invoice total */}
                                    {item.total !== undefined && (
                                      <div className="mt-2">
                                        <span className="text-lg font-bold">${item.total.toFixed(2)}</span>
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                                    {formatRelativeTime(item.timestamp)}
                                  </span>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="files" className="mt-0">
                <ScrollArea className="h-[500px]">
                  {attachments.length === 0 ? (
                    <div className="text-center py-12">
                      <Paperclip className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
                      <h3 className="text-lg font-medium mb-1">No files attached</h3>
                      <p className="text-muted-foreground mb-4">
                        Upload lab results, images, or other documents
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {attachments.map((file) => (
                        <Card key={file.id} className="card-interactive" data-testid={`file-${file.id}`}>
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="p-2 rounded-lg bg-muted">
                              <Paperclip className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{file.filename}</p>
                              <p className="text-sm text-muted-foreground">
                                Uploaded by {file.uploaded_by_name} • {formatDateTime(file.uploaded_at)}
                              </p>
                            </div>
                            <Button variant="outline" size="sm">
                              Download
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Patient</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {patient.first_name} {patient.last_name}? 
              This action cannot be undone and will remove all associated records.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
